import { JsonPipe } from '@angular/common';
import { Component, inject, model, signal } from '@angular/core';
import { DxCheckBoxModule, DxFormModule } from 'devextreme-angular';
import validationEngine from 'devextreme/ui/validation_engine';
import { ApplicationInfoService } from '../core/application-info.service';
import { AuthService } from '../core/auth.service';
import { DialogService } from '../core/dialog.service';

@Component({
    selector: 'app-register',
    imports: [DxFormModule, DxCheckBoxModule],
    template: `
        <div class="master-column-wrapper">
            <div class="center-1">
                <div class="page contact-page">
                    <div class="page-title">
                        <h1>{{ !isSubmitted() ? 'Customer Registration' : 'Thank You' }}</h1>
                    </div>
                    <div class="page-body">
                        @if(isSubmitted()) {
                        <h3>
                            <p>Thank you for registering! You will receive a confirmation email with your account information.</p>
                        </h3>
                        } @else {
                        <h3>
                            <!-- text inviting user to register as a customer -->
                            <p>Thank you for choosing {{ appInfo.info.companyName }}.</p>
                            <p>To register for a user account, please fill out the form below and click submit.</p>
                            <p>After you have received our confirmation email you will be able to log in to your account.</p>
                        </h3>
                        <dx-form
                            [formData]="customer"
                            validationGroup="mainValidationGroup"
                            [colCount]="2"
                            [showColonAfterLabel]="false"
                            style="margin-top: 20px"
                        >
                            <dxi-item itemType="group" caption="Contact Information" [colCount]="2" colSpan="2">
                                <dxi-item itemType="group">
                                    <dxi-item dataField="companyName" [label]="{ text: 'Company Name' }" [isRequired]="true"></dxi-item>
                                    <dxi-item dataField="accountNumber"></dxi-item>
                                    <dxi-item dataField="contactName"></dxi-item>
                                </dxi-item>
                                <dxi-item itemType="group">
                                    <dxi-item dataField="email" [isRequired]="true"></dxi-item>
                                    <dxi-item dataField="phone" [isRequired]="true"></dxi-item>
                                    <dxi-item dataField="fax"></dxi-item>
                                </dxi-item>
                            </dxi-item>
                            <dxi-item itemType="group" caption="Main Address">
                                <dxi-item dataField="mainAddress.address1" [label]="{ text: 'Address' }"></dxi-item>
                                <dxi-item dataField="mainAddress.city" [label]="{ text: 'City' }"> </dxi-item>
                                <dxi-item itemType="group" [colCount]="2">
                                    <dxi-item dataField="mainAddress.state" [label]="{ text: 'State' }"></dxi-item>
                                    <dxi-item dataField="mainAddress.zipCode" [label]="{ text: 'Postal Code' }"> </dxi-item>
                                </dxi-item>
                                <dxi-item dataField="mainAddress.country" [label]="{ text: 'Country' }"> </dxi-item>
                            </dxi-item>
                            <dxi-item itemType="group" caption="Shipping Address" captionTemplate="shippingCaptionTemplate">
                                <dxi-item
                                    dataField="shippingAddress.address1"
                                    [label]="{ text: 'Address' }"
                                    [disabled]="customer.shippingAddressSameAsMain"
                                ></dxi-item>
                                <dxi-item
                                    dataField="shippingAddress.city"
                                    [label]="{ text: 'City' }"
                                    [disabled]="customer.shippingAddressSameAsMain"
                                >
                                </dxi-item>
                                <dxi-item itemType="group" [colCount]="2">
                                    <dxi-item
                                        dataField="shippingAddress.state"
                                        [label]="{ text: 'State' }"
                                        [disabled]="customer.shippingAddressSameAsMain"
                                    ></dxi-item>
                                    <dxi-item
                                        dataField="shippingAddress.zipCode"
                                        [label]="{ text: 'Postal Code' }"
                                        [disabled]="customer.shippingAddressSameAsMain"
                                    >
                                    </dxi-item>
                                </dxi-item>
                                <dxi-item
                                    dataField="shippingAddress.country"
                                    [label]="{ text: 'Country' }"
                                    [disabled]="customer.shippingAddressSameAsMain"
                                >
                                </dxi-item>
                            </dxi-item>
                            <dxi-item itemType="group" caption="Additional Information">
                                <dxi-item
                                    dataField="additionalInformation"
                                    [editorType]="'dxTextArea'"
                                    [editorOptions]="{ height: '100px', elementAttr: { height: '100px' } }"
                                    [label]="{ text: 'Any comments or information you want to share' }"
                                ></dxi-item>
                                <dxi-item
                                    dataField="acceptTerms"
                                    [editorType]="'dxCheckBox'"
                                    [label]="{ text: 'Accept Terms and Conditions', location: 'left' }"
                                    [editorOptions]="{ text: '' }"
                                    [isRequired]="true"
                                ></dxi-item>
                                <dxi-item
                                    dataField="optIn"
                                    [editorType]="'dxCheckBox'"
                                    [label]="{ text: 'Sign up for all of our latest product news and special offers!', location: 'left' }"
                                    [editorOptions]="{ text: '' }"
                                ></dxi-item>
                            </dxi-item>
                            <dxi-item itemType="group" caption="User Login Information">
                                <dxi-item dataField="loginEmail" [label]="{ text: 'Email' }" [isRequired]="true">
                                    <dxi-validation-rule type="required" message="Email is required"></dxi-validation-rule>
                                    <dxi-validation-rule type="email" message="Email is not valid"></dxi-validation-rule>
                                </dxi-item>
                                <dxi-item
                                    dataField="loginPassword"
                                    [label]="{ text: 'Password' }"
                                    [editorOptions]="{ mode: 'password' }"
                                    [isRequired]="true"
                                ></dxi-item>
                                <dxi-item
                                    dataField="loginPasswordConfirm"
                                    [label]="{ text: 'Confirm Password' }"
                                    [editorOptions]="{ mode: 'password' }"
                                    [isRequired]="true"
                                >
                                    <dxi-validation-rule
                                        type="compare"
                                        [comparisonTarget]="comparePassword"
                                        message="Passwords do not match"
                                    ></dxi-validation-rule>
                                </dxi-item>
                            </dxi-item>

                            <div *dxTemplate="let data of 'shippingCaptionTemplate'" class="shipping-caption">
                                <div>
                                    {{ data.caption }}
                                </div>
                                <dx-check-box
                                    [(value)]="customer.shippingAddressSameAsMain"
                                    text="Same as Main Address"
                                    (valueChange)="shippingAddressSameAsMainChanged($event)"
                                    [disabled]="false"
                                ></dx-check-box>
                            </div>
                        </dx-form>
                        <button type="button" class="button-1" (click)="submit()" [disabled]="processing()">Submit</button>
                        <!-- <div style="margin-top: 20px;">{{ customer | json }}</div> -->
                        }
                    </div>
                </div>
            </div>
        </div>
    `,
    styles: `
        .shipping-caption {
            display: flex;
            align-items: center;
            gap: 20px;
        }
    `
})
export class RegisterComponent {
    auth = inject(AuthService);
    dialogService = inject(DialogService);
    appInfo = inject(ApplicationInfoService);

    isSubmitted = signal(false);
    processing = signal(false);

    customer: any = {};

    ngOnInit() {
        this.customer = {
            mainAddress: {},
            shippingAddress: {},
            shippingAddressSameAsMain: false,
            acceptTerms: false,
            optIn: false,
            loginEmail: '',
            loginPassword: '',
            loginPasswordConfirm: ''
        };

        if (location.href.indexOf('localhost') > 0) {
            this.customer = {
                companyName: 'PEC',
                accountNumber: '1234',
                contactName: 'Tim',
                email: 'test@pec.com',
                phone: '616-776-1745',
                // mainAddress: { address: '77 Monroe Center NW, Suite 600', city: 'Grand Rapids', state: 'MI', zipCode: '49503', country: 'US' },
                mainAddress: {
                    address: '123 Main St',
                    city: 'Anytown',
                    state: 'CA',
                    zipCode: '12345',
                    country: 'US'
                },
                shippingAddressSameAsMain: false
            };

            this.customer.shippingAddress = { ...this.customer.mainAddress };
        }
    }

    async submit() {
        if (!this.validate()) {
            return;
        }
        const result = await this.auth.register(this.customer);
        if (!result.isSuccess) {
            console.log('Register error', result);
            let errorMessages = '';
            if (result.errors) {
                errorMessages = Object.keys(result.errors)
                    .map(
                        (key) =>
                            `<div class="dialog-text">
                            <b>${key}</b><br>
                            <ul>
                                ${(result.errors as any)![key].map((error: string) => `<li>${error}</li>`).join('\n')}
                            </ul>
                            </div>`
                    )
                    .join('<br>');
            }
            this.dialogService.alert(
                errorMessages || result.detail || result.message || 'Error registering customer',
                'Registration failed',
                true
            );
            return;
        }
        this.isSubmitted.set(true);
    }

    shippingAddressSameAsMainChanged(value: boolean | null | undefined) {
        if (value) {
            this.customer.shippingAddress = { ...this.customer.mainAddress };
        } else {
            this.customer.shippingAddress = {};
        }
    }

    validate() {
        const result = validationEngine.validateGroup('mainValidationGroup');
        if (!result.isValid) {
            this.dialogService.alert('Please correct the errors in the form.', 'Validation Error', true);
            return false;
        }
        return result.isValid;
    }

    comparePassword = () => this.customer.loginPassword;
}
